import { FormSelect } from "../../../components/ui/select";
import {
  ChevronDown,
  GripVertical,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { Textarea } from "../../../components/ui/textarea";
import { getQuotationPdfConfig, updateQuotationPdfConfig } from "../../../services/quotationService";
import { fetchRoles } from "../../../services/rolesPermissionsService";
import {
  createPmcConfig,
  createPmcConfigLine,
  createTerm,
  deletePmcConfig,
  deleteApprovalRule,
  deleteTerm,
  getApprovalRules,
  getPmcConfigs,
  getAllTerms,
  updatePmcConfig,
  updatePmcConfigLine,
  updateTerm,
  updateNetPriceRule,
} from "../../../services/salesConfigService";
import sessionDB from "../../../utils/sessionDB";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import {
  sq,
  localizedFieldProps,
  extractNumericId,
  extractObjectPayload,
  mapApiNetPriceRuleToUi,
  extractApiApprovalRules,
  dedupeApprovalRules,
  persistApprovalRule,
  APPROVAL_RULE_KIND_OPTIONS,
  createEmptyApprovalRuleDraft,
  inferApprovalRuleKind,
  formatRuleThresholdSummary,
  buildApprovalRulesFromDraft,
  sortApprovalRules,
  buildPmcHeadPresentation,
  resolvePmcLineLabel,
  resolvePmcLineId,
  createBlankPmcLine,
  getPmcHeadTypeMetricFields,
  pickPmcLineMetricFields,
  pickPmcHeadMetricFields,
  buildPmcHeadMetricPayload,
  applyHeadMetricsToLines,
  normalizePmcLineEntries,
  buildPmcConfigLinesPayload,
  canonicalizePmcHeadType,
  PMC_COST_HEAD_TYPE_OPTIONS,
  createEmptyPmcCreateForm,
  buildPmcCreateApiPayload,
  isPersistedPmcHead,
  extractApiPmcConfigs,
  buildPmcConfigPayload,
  applySavedRowsToHeads,
  sortTermsForUi,
  TERM_QUOTATION_TYPE_VALUES,
  normalizeTermQuotationTypes,
  buildTermApiPayload,
  toggleTermQuotationType,
  termQuotationTypeLabel,
  extractApiTerms,
  PDF_TEMPLATE_BODY_MAX,
  createEmptyPdfImageSlot,
  pdfImageSlotToUploadFile,
  mergePdfSlotAfterSave,
  normalizeQuotationPdfConfig,
  revokePdfImageObjectUrl,
  validatePdfTemplateImageFile,
} from "./quotationUtils";
import { QuotationSecondaryButton, TableSkeleton, QuotationConfirmModal } from "./QuotationComponents";
import { PdfTemplateTab } from "./QuotationPdfTemplateTab";


function ConfigEmptyState({ title, body, actionLabel, onAction }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#d8e0ed] bg-[#fafcff] px-5 py-8 text-center">
      <p className="text-[15px] font-semibold text-[#23324c]">{title}</p>
      <p className="mt-2 text-[13px] leading-6 text-[#7c8faa]">{body}</p>
      {actionLabel && onAction && (
        <div className="mt-4">
          <QuotationSecondaryButton icon={Plus} onClick={onAction}>
            {actionLabel}
          </QuotationSecondaryButton>
        </div>
      )}
    </div>
  );
}

function QuotationConfigurationWorkspace({ activeSection }) {
  const isMobileLayout = useIsMobileLayout();
  const [heads, setHeads] = useState([]);
  const [rules, setRules] = useState([]);
  const [terms, setTerms] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);
  const [approverOptionsLoading, setApproverOptionsLoading] = useState(false);
  const [approverOptionsError, setApproverOptionsError] = useState("");
  const [banner, setBanner] = useState("");
  const [addTermFormOpen, setAddTermFormOpen] = useState(false);
  const [termDraft, setTermDraft] = useState({ id: "", title: "", text: "", order: 1, status: "Active", quotationTypes: [] });
  const [addRuleFormOpen, setAddRuleFormOpen] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(() => createEmptyApprovalRuleDraft());
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [ruleSaving, setRuleSaving] = useState(false);
  const [ruleSavingId, setRuleSavingId] = useState(null);
  const [ruleDeletingId, setRuleDeletingId] = useState(null);
  const [headsLoading, setHeadsLoading] = useState(false);
  const [headsError, setHeadsError] = useState("");
  const [headSaving, setHeadSaving] = useState(false);
  const [headDeletingId, setHeadDeletingId] = useState(null);
  const [addPmcFormOpen, setAddPmcFormOpen] = useState(false);
  const [pmcCreateForm, setPmcCreateForm] = useState(() => createEmptyPmcCreateForm());
  /** Mobile-only: which PMC cost heads are expanded (independent toggles). */
  const [expandedPmcHeadIds, setExpandedPmcHeadIds] = useState(() => new Set());
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [termSaving, setTermSaving] = useState(false);
  const [termDeletingId, setTermDeletingId] = useState(null);
  const [termDeleteTarget, setTermDeleteTarget] = useState(null);
  const [netPriceConfig, setNetPriceConfig] = useState({ id: "", tenantId: "", netPricePercentage: "" });
  const [netPriceLoading, setNetPriceLoading] = useState(false);
  const [netPriceSaving, setNetPriceSaving] = useState(false);
  const [netPriceError, setNetPriceError] = useState("");
  const [pdfHeaderImage, setPdfHeaderImage] = useState(() => createEmptyPdfImageSlot());
  const [pdfFooterImage, setPdfFooterImage] = useState(() => createEmptyPdfImageSlot());
  const [pdfBodyContent, setPdfBodyContent] = useState("");
  const [pdfTemplateErrors, setPdfTemplateErrors] = useState({});
  const [pdfTemplateLoading, setPdfTemplateLoading] = useState(false);
  const [pdfTemplateSaving, setPdfTemplateSaving] = useState(false);
  const [pdfTemplateLoadError, setPdfTemplateLoadError] = useState("");
  const [pdfTemplateSaveError, setPdfTemplateSaveError] = useState("");
  const pdfConfigLoadedRef = useRef(false);

  function showSuccess(message) {
    setBanner(message);
    window.setTimeout(() => setBanner(""), 2600);
  }

  function applyApprovalRulesFromResponse(data) {
    const nextRules = dedupeApprovalRules(extractApiApprovalRules(data));
    setRules(nextRules);
    return nextRules;
  }

  useEffect(() => {
    let active = true;
    setApproverOptionsLoading(true);
    setApproverOptionsError("");

    fetchRoles()
      .then((roles) => {
        if (!active) return;
        const nextOptions = roles
          .map((role) => String(role.name || "").trim())
          .filter(Boolean);
        setApproverOptions(Array.from(new Set(nextOptions)));
        if (!nextOptions.length) {
          setApproverOptionsError("No tenant roles were returned by the roles API.");
        }
      })
      .catch((error) => {
        if (!active) return;
        setApproverOptions([]);
        setApproverOptionsError(error.message || "Unable to load approval levels from the roles API.");
      })
      .finally(() => {
        if (!active) return;
        setApproverOptionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (activeSection !== "pmc") {
      return () => {
        active = false;
      };
    }

    async function loadPmcConfigs() {
      setHeadsLoading(true);
      setHeadsError("");

      try {
        const response = await getPmcConfigs();
        if (!active) return;
        const nextHeads = extractApiPmcConfigs(response?.data).map((head, index) => ({
          ...head,
          order: index + 1,
          dirty: false,
        }));
        setHeads(nextHeads);
      } catch (error) {
        if (!active) return;
        setHeadsError(error.message || "Failed to load PMC configurations.");
      } finally {
        if (active) setHeadsLoading(false);
      }
    }

    loadPmcConfigs();
    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "terms") {
      return () => {
        active = false;
      };
    }

    async function loadTermsConfig() {
      setTermsLoading(true);
      setTermsError("");

      try {
        const response = await getAllTerms();
        if (!active) return;
        setTerms(sortTermsForUi(extractApiTerms(response?.data)));
      } catch (error) {
        if (!active) return;
        setTermsError(error.message || "Failed to load standard terms.");
      } finally {
        if (active) setTermsLoading(false);
      }
    }

    loadTermsConfig();
    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "rules") {
      return () => {
        active = false;
      };
    }

    async function loadRuleConfigs() {
      setRulesLoading(true);
      setRulesError("");

      try {
        const rulesResponse = await getApprovalRules();
        if (!active) return;
        applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      } catch (error) {
        if (!active) return;
        setRulesError(error.message || "Failed to load sales configuration.");
      } finally {
        if (active) setRulesLoading(false);
      }
    }

    loadRuleConfigs();
    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "netPrice") {
      return () => {
        active = false;
      };
    }

    setNetPriceLoading(false);
    setNetPriceError("");
    setNetPriceConfig((current) => ({
      ...current,
      tenantId: current.tenantId || String(sessionDB.getTenantId() || ""),
    }));

    return () => {
      active = false;
    };
  }, [activeSection]);

  useEffect(() => {
    let active = true;

    if (activeSection !== "pdf") {
      pdfConfigLoadedRef.current = false;
      return () => {
        active = false;
      };
    }

    async function loadPdfDownloadRules() {
      if (pdfConfigLoadedRef.current) return;
      setPdfTemplateLoading(true);
      setPdfTemplateLoadError("");
      setPdfTemplateSaveError("");
      try {
        const response = await getQuotationPdfConfig();
        if (!active) return;
        const normalized = normalizeQuotationPdfConfig(response?.data ?? response ?? {});
        setPdfHeaderImage((current) => {
          revokePdfImageObjectUrl(current);
          return normalized.headerImage;
        });
        setPdfFooterImage((current) => {
          revokePdfImageObjectUrl(current);
          return normalized.footerImage;
        });
        setPdfBodyContent(normalized.bodyContent);
        pdfConfigLoadedRef.current = true;
      } catch (error) {
        if (!active) return;
        setPdfTemplateLoadError(error.message || sq("pdfTemplateLoadFailed"));
      } finally {
        if (active) setPdfTemplateLoading(false);
      }
    }

    loadPdfDownloadRules();
    return () => {
      active = false;
    };
  }, [activeSection]);

  function patchRuleLocal(ruleId, updates) {
    setRules((current) =>
      current.map((rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule))
    );
  }

  function openAddRuleForm() {
    setRuleDraft(createEmptyApprovalRuleDraft(approverOptions));
    setAddRuleFormOpen(true);
    setRulesError("");
  }

  function closeAddRuleForm() {
    setAddRuleFormOpen(false);
    setRuleDraft(createEmptyApprovalRuleDraft(approverOptions));
  }

  function patchRuleDraft(updates) {
    setRuleDraft((current) => ({ ...current, ...updates }));
  }

  function validateApprovalRuleDraft(draft) {
    const kind = String(draft.ruleKind || "").trim();
    if (!kind) return "Select which approval rule applies.";

    if (draft.needsApproval && !String(draft.approvalLevel || "").trim()) {
      return "Select an Approver when approval is required.";
    }

    return "";
  }

  async function saveAddRuleForm() {
    if (ruleSaving) return;

    const validationError = validateApprovalRuleDraft(ruleDraft);
    if (validationError) {
      setRulesError(validationError);
      return;
    }

    const draftsToSave = buildApprovalRulesFromDraft(ruleDraft, {
      startOrder: rules.length + 1,
    });
    if (!draftsToSave.length) {
      setRulesError("Select which approval rule applies.");
      return;
    }

    setRuleSaving(true);
    setRuleSavingId("new-rule");
    setRulesError("");

    try {
      for (const draftRule of draftsToSave) {
        await persistApprovalRule(draftRule, { previousId: 0 });
      }
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      closeAddRuleForm();
      showSuccess(
        draftsToSave.length > 1 ? "Approval rules saved." : "Approval rule saved."
      );
    } catch (error) {
      setRulesError(error.message || "Failed to save approval rule.");
    } finally {
      setRuleSavingId(null);
      setRuleSaving(false);
    }
  }

  async function saveRuleInline(rule) {
    if (!rule) return;

    if (!rule.autoApprove && !String(rule.approvalLevel || "").trim()) {
      setRulesError("Select an approver for rules that need approval.");
      return;
    }

    const previousId = rule.isDraft ? 0 : extractNumericId(rule.id) || 0;

    setRuleSavingId(rule.id);
    setRuleSaving(true);
    setRulesError("");

    try {
      const savedRule = await persistApprovalRule(rule, { previousId });
      if (savedRule) {
        setRules((current) =>
          dedupeApprovalRules(
            sortApprovalRules([
              ...current.filter((item) => item.id !== rule.id && Number(item.id) !== previousId),
              savedRule,
            ])
          )
        );
      }
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      showSuccess(rule.isDraft ? "Approval rule saved." : "Approval rule updated.");
    } catch (error) {
      setRulesError(error.message || "Failed to save approval rule.");
    } finally {
      setRuleSavingId(null);
      setRuleSaving(false);
    }
  }

  function moveItem(list, setList, index, direction, orderKey = "order") {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    const swap = next[index];
    next[index] = next[target];
    next[target] = swap;
    setList(next.map((item, itemIndex) => ({ ...item, [orderKey]: itemIndex + 1 })));
    showSuccess("Configuration order updated.");
  }

  function toggleStatus(list, setList, id) {
    setList((current) =>
      current.map((item) => item.id === id ? { ...item, isActive: !item.isActive } : item)
    );
    showSuccess("Configuration status updated.");
  }

  function openAddPmcForm() {
    setPmcCreateForm(createEmptyPmcCreateForm());
    setAddPmcFormOpen(true);
    setHeadsError("");
  }

  function closeAddPmcForm() {
    setAddPmcFormOpen(false);
    setPmcCreateForm(createEmptyPmcCreateForm());
  }

  function patchPmcCreateForm(updates) {
    setPmcCreateForm((current) => {
      const next = { ...current, ...updates };
      const metricKeys = getPmcHeadTypeMetricFields(next.costHeadType).map((field) => field.key);
      const metricsChanged = metricKeys.some((key) => Object.prototype.hasOwnProperty.call(updates, key));
      if (!metricsChanged && !Object.prototype.hasOwnProperty.call(updates, "costHeadType")) {
        return next;
      }
      const metrics = pickPmcHeadMetricFields(next, next.costHeadType);
      return {
        ...next,
        ...metrics,
        defaultRows: applyHeadMetricsToLines(
          next.defaultRows,
          metrics,
          next.costHeadType,
          { markDirty: false }
        ),
      };
    });
  }

  function addPmcCreateLine() {
    setPmcCreateForm((current) => {
      const metrics = pickPmcHeadMetricFields(current, current.costHeadType);
      return {
        ...current,
        defaultRows: [
          ...normalizePmcLineEntries(current.defaultRows, current.costHeadType),
          createBlankPmcLine({ dirty: true, isNew: true, ...metrics }),
        ],
      };
    });
  }

  function updatePmcCreateLine(index, value) {
    setPmcCreateForm((current) => ({
      ...current,
      defaultRows: normalizePmcLineEntries(current.defaultRows, current.costHeadType).map((row, rowIndex) => (
        rowIndex === index
          ? { ...row, label: value, dirty: true }
          : row
      )),
    }));
  }

  function removePmcCreateLine(index) {
    setPmcCreateForm((current) => {
      const nextRows = normalizePmcLineEntries(current.defaultRows, current.costHeadType)
        .filter((_, rowIndex) => rowIndex !== index);
      return {
        ...current,
        defaultRows: nextRows.length ? nextRows : [createBlankPmcLine({ dirty: true, isNew: true })],
      };
    });
  }

  async function createPmcCostHead() {
    if (headSaving) return;

    const formPayload = buildPmcCreateApiPayload(pmcCreateForm);
    if (!formPayload.headName) {
      setHeadsError("Head Name is required.");
      return;
    }
    if (!formPayload.costHeadType) {
      setHeadsError("Head Type is required.");
      return;
    }

    const headMetrics = pickPmcHeadMetricFields(pmcCreateForm, formPayload.costHeadType);
    const lineEntries = applyHeadMetricsToLines(
      pmcCreateForm.defaultRows,
      headMetrics,
      formPayload.costHeadType
    ).filter((row) => String(row.label || "").trim());

    // Head create payload — lines are added separately via the Add Line API.
    const headPayload = {
      id: 0,
      headName: formPayload.headName,
      costHeadType: formPayload.costHeadType,
      isActive: formPayload.isActive,
      ...buildPmcHeadMetricPayload(headMetrics, formPayload.costHeadType),
    };

    setHeadSaving(true);
    setHeadsError("");

    try {
      let savedId = 0;
      try {
        const createResponse = await createPmcConfig(headPayload);
        savedId = resolvePmcIdFromResponse(createResponse);
      } catch (error) {
        throw new Error(error?.message || "Failed to create PMC cost head.");
      }

      if (!savedId) {
        throw new Error("PMC cost head was created, but no config ID was returned.");
      }

      for (const line of lineEntries) {
        try {
          await createPmcConfigLine(
            buildPmcConfigLinesPayload(
              savedId,
              [line],
              { isUpdate: false, costHeadType: formPayload.costHeadType }
            )
          );
        } catch (error) {
          throw new Error(error?.message || `Failed to add PMC line item "${line.label}".`);
        }
      }

      await refreshPmcHeads();
      closeAddPmcForm();
      showSuccess("PMC cost head created.");
    } catch (error) {
      setHeadsError(error.message || "Failed to create PMC cost head.");
    } finally {
      setHeadSaving(false);
    }
  }

  async function refreshPmcHeads({ savedRowsById = {}, keepDraftId } = {}) {
    const response = await getPmcConfigs();
    const nextHeads = applySavedRowsToHeads(
      extractApiPmcConfigs(response?.data).map((head, index) => ({
        ...head,
        order: index + 1,
      })),
      savedRowsById
    );
    setHeads(nextHeads.filter((head) => isPersistedPmcHead(head) || head.id === keepDraftId));
    return nextHeads;
  }

  function patchHeadLocal(headId, updater) {
    setHeads((current) =>
      current.map((head) => {
        if (head.id !== headId) return head;
        const next = typeof updater === "function" ? updater(head) : { ...head, ...updater };
        return { ...next, dirty: true };
      })
    );
  }

  function addBreakdownRow(headId) {
    patchHeadLocal(headId, (head) => {
      const metrics = pickPmcHeadMetricFields(head, head.costHeadType);
      return {
        ...head,
        defaultRows: [
          ...normalizePmcLineEntries(head.defaultRows, head.costHeadType),
          createBlankPmcLine({ dirty: true, isNew: true, ...metrics }),
        ],
      };
    });
  }

  function updateBreakdownRow(headId, rowIndex, value) {
    patchHeadLocal(headId, (head) => ({
      ...head,
      defaultRows: normalizePmcLineEntries(head.defaultRows, head.costHeadType).map((row, index) => (
        index === rowIndex
          ? { ...row, label: value, dirty: true }
          : row
      )),
    }));
  }

  function updateHeadMetricField(headId, field, value) {
    patchHeadLocal(headId, (head) => {
      const nextMetrics = {
        ...pickPmcHeadMetricFields(head, head.costHeadType),
        [field]: value,
      };
      return {
        ...head,
        ...nextMetrics,
        defaultRows: applyHeadMetricsToLines(
          head.defaultRows,
          nextMetrics,
          head.costHeadType,
          { markDirty: true }
        ),
        dirty: true,
      };
    });
  }

  function removeBreakdownRow(headId, rowIndex) {
    patchHeadLocal(headId, (head) => {
      const currentRows = normalizePmcLineEntries(head.defaultRows, head.costHeadType);
      const nextRows = currentRows.filter((_, index) => index !== rowIndex);
      return {
        ...head,
        defaultRows: nextRows.length ? nextRows : [createBlankPmcLine({ dirty: true, isNew: true })],
      };
    });
  }

  function resolvePmcIdFromResponse(response, fallbackId = 0) {
    const payload =
      extractObjectPayload(response?.data) ||
      extractObjectPayload(response) ||
      null;
    return extractNumericId(payload?.id ?? payload?.pmcConfigId) || extractNumericId(fallbackId) || 0;
  }

  function mergePmcLinePersistedState(source = {}, persisted = {}, costHeadType = "") {
    const metrics = pickPmcLineMetricFields(
      {
        ...source,
        ...persisted,
        // Prefer response metrics when present; otherwise keep the values just saved.
        boqQty: persisted.boqQty ?? persisted.boq_qty ?? source.boqQty,
        unitRate: persisted.unitRate ?? persisted.unit_rate ?? source.unitRate,
        consQty: persisted.consQty ?? persisted.consumedQty ?? persisted.cons_qty ?? source.consQty,
        nos: persisted.nos ?? source.nos,
        months: persisted.months ?? source.months,
        ctc: persisted.ctc ?? persisted.rate ?? source.ctc,
      },
      costHeadType
    );
    const id = resolvePmcLineId(persisted) || persisted.id || source.id || null;
    return createBlankPmcLine({
      clientId: source.clientId || persisted.clientId,
      id,
      label: String(persisted.label || source.label || "").trim(),
      dirty: false,
      isNew: !id,
      isActive: source.isActive !== false && persisted.isActive !== false,
      ...metrics,
    });
  }

  function mapPersistedLinesFromResponse(response, sourceLines = [], costHeadType = "") {
    const payload =
      extractObjectPayload(response?.data) ||
      extractObjectPayload(response) ||
      null;
    const responseLines = normalizePmcLineEntries(
      payload?.lines ?? payload?.lineItems ?? payload?.defaultRows ?? [],
      costHeadType
    ).filter((row) => row.label || row.id);

    if (responseLines.length) {
      return responseLines.map((row, index) =>
        mergePmcLinePersistedState(sourceLines[index] || {}, row, costHeadType)
      );
    }

    // Single-line update/add responses may return the line object at the root.
    const rootLineId = resolvePmcLineId(payload);
    if (rootLineId && sourceLines.length === 1) {
      return [
        mergePmcLinePersistedState(
          sourceLines[0],
          {
            ...payload,
            id: rootLineId,
            label: resolvePmcLineLabel(payload) || sourceLines[0].label,
          },
          costHeadType
        ),
      ];
    }

    return sourceLines.map((line) => mergePmcLinePersistedState(line, line, costHeadType));
  }

  async function persistPmcLines(pmcConfigId, lines = [], costHeadType = "") {
    const configId = Number(pmcConfigId);
    if (!Number.isFinite(configId) || configId <= 0) {
      throw new Error("Invalid PMC cost head id for line items.");
    }

    const filledLines = normalizePmcLineEntries(lines, costHeadType)
      .filter((line) => String(line.label || "").trim());

    if (!filledLines.length) {
      return [createBlankPmcLine({ dirty: false, isNew: true })];
    }

    const linesToAdd = filledLines.filter((line) => !(Number(line.id) > 0));
    const linesToUpdate = filledLines.filter((line) => Number(line.id) > 0 && line.dirty);
    const unchangedLines = filledLines.filter((line) => Number(line.id) > 0 && !line.dirty);

    const persistedByClientId = new Map();

    unchangedLines.forEach((line) => {
      persistedByClientId.set(line.clientId, mergePmcLinePersistedState(line, line, costHeadType));
    });

    // New rows (including rows added while editing an existing head) → Add Line API
    if (linesToAdd.length) {
      try {
        const response = await createPmcConfigLine(
          buildPmcConfigLinesPayload(configId, linesToAdd, { isUpdate: false, costHeadType })
        );
        mapPersistedLinesFromResponse(response, linesToAdd, costHeadType).forEach((line, index) => {
          const source = linesToAdd[index] || line;
          persistedByClientId.set(source.clientId, mergePmcLinePersistedState(source, line, costHeadType));
        });
      } catch (error) {
        throw new Error(error?.message || "Failed to add PMC line item.");
      }
    }

    // Existing edited rows → Update Line API
    // PUT /saas/crm/pmc-configs/lines/update/{pmcConfigId}
    // body: { id: pmcConfigId, lines: [{ id, pmcConfigId, lineName, isActive, ...headTypeFields }] }
    if (linesToUpdate.length) {
      const normalizedUpdates = linesToUpdate.map((line) => {
        const lineId = resolvePmcLineId(line) || Number(line.id);
        if (!Number.isFinite(lineId) || lineId <= 0) {
          throw new Error("Invalid PMC line item id.");
        }
        return { ...line, id: lineId };
      });

      try {
        const response = await updatePmcConfigLine(
          configId,
          buildPmcConfigLinesPayload(configId, normalizedUpdates, { isUpdate: true, costHeadType })
        );
        mapPersistedLinesFromResponse(response, normalizedUpdates, costHeadType).forEach((line, index) => {
          const source = normalizedUpdates[index] || line;
          persistedByClientId.set(source.clientId, mergePmcLinePersistedState(source, line, costHeadType));
        });
      } catch (error) {
        throw new Error(error?.message || "Failed to update PMC line item.");
      }
    }

    const ordered = filledLines.map((line) =>
      persistedByClientId.get(line.clientId) || mergePmcLinePersistedState(line, line, costHeadType)
    );

    return ordered.length ? ordered : [createBlankPmcLine({ dirty: false, isNew: true })];
  }

  async function saveHeadInline(head) {
    if (!head || headSaving) return;
    if (!isPersistedPmcHead(head)) {
      setHeadsError("Use the Create form to add a new PMC cost head.");
      return;
    }

    const presentation = buildPmcHeadPresentation(head.name, head.category);
    const costHeadType = head.costHeadType || head.category || presentation.category;
    const headMetrics = pickPmcHeadMetricFields(head, costHeadType);
    // Always push Cost Head-level metrics onto lines before Add/Update Line APIs.
    const lines = applyHeadMetricsToLines(
      head.defaultRows,
      headMetrics,
      costHeadType,
      { markDirty: true }
    );
    const normalized = {
      ...head,
      ...headMetrics,
      category: head.category || presentation.category,
      costHeadType,
      applicableQuotationType: head.applicableQuotationType || "All",
      defaultRows: lines,
      description: head.description || presentation.description,
      status: head.status === "Inactive" ? "Inactive" : "Active",
    };

    if (!String(normalized.name || "").trim()) {
      setHeadsError("Cost head name is required.");
      return;
    }

    const savedId = Number(head.id);
    setHeadSaving(true);
    setHeadsError("");

    try {
      const payload = buildPmcConfigPayload(normalized);
      try {
        await updatePmcConfig(savedId, { ...payload, id: savedId });
      } catch (error) {
        throw new Error(error?.message || "Failed to update PMC cost head.");
      }

      const savedLines = await persistPmcLines(savedId, lines, costHeadType);

      await refreshPmcHeads({
        savedRowsById: { [String(savedId)]: savedLines },
      });
      showSuccess("PMC cost configuration updated.");
    } catch (error) {
      setHeadsError(error.message || "Failed to save PMC configuration.");
    } finally {
      setHeadSaving(false);
    }
  }

  async function toggleHeadStatus(head) {
    if (!head?.id || headSaving) return;

    const nextStatus = head.status === "Active" ? "Inactive" : "Active";
    setHeads((current) =>
      current.map((item) => (
        item.id === head.id ? { ...item, status: nextStatus } : item
      ))
    );

    if (!isPersistedPmcHead(head)) return;

    setHeadSaving(true);
    setHeadsError("");

    try {
      await updatePmcConfig(
        Number(head.id),
        buildPmcConfigPayload({ ...head, status: nextStatus })
      );
      await refreshPmcHeads();
      showSuccess(nextStatus === "Active" ? "PMC cost head activated." : "PMC cost head deactivated.");
    } catch (error) {
      setHeads((current) =>
        current.map((item) => (
          item.id === head.id ? { ...item, status: head.status } : item
        ))
      );
      setHeadsError(error.message || "Failed to update PMC status.");
    } finally {
      setHeadSaving(false);
    }
  }

  async function removeHead(id) {
    if (!id || headDeletingId) return;

    const head = heads.find((item) => item.id === id);
    if (!isPersistedPmcHead(head)) {
      setHeads((current) => current.filter((item) => item.id !== id).map((item, index) => ({ ...item, order: index + 1 })));
      return;
    }

    if (!window.confirm("Delete this PMC cost head? This cannot be undone.")) return;

    setHeadDeletingId(id);
    setHeadsError("");

    try {
      await deletePmcConfig(Number(id));
      setHeads((current) => current.filter((item) => item.id !== id).map((item, index) => ({ ...item, order: index + 1 })));
      showSuccess("PMC cost configuration deleted.");
    } catch (error) {
      setHeadsError(error.message || "Failed to delete PMC configuration.");
    } finally {
      setHeadDeletingId(null);
    }
  }

  async function removeRule(id) {
    if (!id) return;

    if (String(id).startsWith("draft-")) {
      setRules((current) =>
        sortApprovalRules(current.filter((item) => item.id !== id))
      );
      return;
    }

    setRuleDeletingId(id);
    setRulesError("");

    try {
      await deleteApprovalRule(Number(id));
      const rulesResponse = await getApprovalRules();
      applyApprovalRulesFromResponse(rulesResponse?.data ?? rulesResponse);
      showSuccess("Approval rule deleted.");
    } catch (error) {
      setRulesError(error.message || "Failed to delete approval rule.");
    } finally {
      setRuleDeletingId(null);
    }
  }

  async function saveNetPriceConfig() {
    const payload = {
      id: Number(netPriceConfig.id) || 0,
      tenantId: String(netPriceConfig.tenantId || sessionDB.getTenantId() || ""),
      netPricePercentage: Number(netPriceConfig.netPricePercentage) || 0,
    };

    setNetPriceSaving(true);
    setNetPriceError("");

    try {
      const updateResponse = await updateNetPriceRule(payload);
      const mapped = mapApiNetPriceRuleToUi(updateResponse?.data ?? updateResponse);
      setNetPriceConfig({
        id: mapped.id || payload.id,
        tenantId: mapped.tenantId || payload.tenantId,
        netPricePercentage:
          mapped.netPricePercentage !== ""
            ? mapped.netPricePercentage
            : String(payload.netPricePercentage),
      });
      showSuccess(sq("netPriceRuleSaved"));
    } catch (error) {
      setNetPriceError(error.message || sq("netPriceRuleSaveFailed"));
    } finally {
      setNetPriceSaving(false);
    }
  }

  async function savePdfDownloadRules() {
    const nextErrors = {};
    if (pdfHeaderImage.file) {
      const headerError = validatePdfTemplateImageFile(pdfHeaderImage.file);
      if (headerError) nextErrors.headerImage = headerError;
    }
    if (pdfFooterImage.file) {
      const footerError = validatePdfTemplateImageFile(pdfFooterImage.file);
      if (footerError) nextErrors.footerImage = footerError;
    }
    const body = String(pdfBodyContent || "");
    if (body.length > PDF_TEMPLATE_BODY_MAX) {
      nextErrors.bodyContent = sq("pdfTemplateBodyTooLong");
    }
    if (Object.keys(nextErrors).length) {
      setPdfTemplateErrors(nextErrors);
      setPdfTemplateSaveError(sq("pdfTemplateValidationFailed"));
      return;
    }

    setPdfTemplateSaving(true);
    setPdfTemplateSaveError("");
    setPdfTemplateErrors({});

    try {
      // Rebuild multipart image parts from new uploads or retained GET/PUT payloads.
      // Backend rejects null headerImage/footerImage when a saved config already exists.
      const headerFile = await pdfImageSlotToUploadFile(pdfHeaderImage, "header-image.png");
      const footerFile = await pdfImageSlotToUploadFile(pdfFooterImage, "footer-image.png");

      const headerRequired =
        Boolean(pdfHeaderImage.existing || pdfHeaderImage.file || pdfHeaderImage.retainedPayload) &&
        !pdfHeaderImage.removed;
      const footerRequired =
        Boolean(pdfFooterImage.existing || pdfFooterImage.file || pdfFooterImage.retainedPayload) &&
        !pdfFooterImage.removed;

      if (headerRequired && !(headerFile instanceof Blob)) {
        setPdfTemplateSaveError(sq("pdfTemplateSaveFailed"));
        setPdfTemplateErrors((current) => ({
          ...current,
          headerImage: sq("pdfTemplateImageRequired"),
        }));
        return;
      }
      if (footerRequired && !(footerFile instanceof Blob)) {
        setPdfTemplateSaveError(sq("pdfTemplateSaveFailed"));
        setPdfTemplateErrors((current) => ({
          ...current,
          footerImage: sq("pdfTemplateImageRequired"),
        }));
        return;
      }

      const response = await updateQuotationPdfConfig({
        headerImage: headerFile instanceof Blob ? headerFile : undefined,
        footerImage: footerFile instanceof Blob ? footerFile : undefined,
        bodyContent: body,
        removeHeaderImage: Boolean(pdfHeaderImage.removed) && !(headerFile instanceof Blob),
        removeFooterImage: Boolean(pdfFooterImage.removed) && !(footerFile instanceof Blob),
      });
      const normalized = normalizeQuotationPdfConfig(response?.data ?? response ?? {});

      const nextHeader = await mergePdfSlotAfterSave(
        pdfHeaderImage,
        normalized.headerImage,
        headerFile
      );
      const nextFooter = await mergePdfSlotAfterSave(
        pdfFooterImage,
        normalized.footerImage,
        footerFile
      );

      setPdfHeaderImage(nextHeader);
      setPdfFooterImage(nextFooter);
      setPdfBodyContent(normalized.bodyContent || body);
      pdfConfigLoadedRef.current = true;
      showSuccess(sq("pdfTemplateSaved"));
    } catch (error) {
      setPdfTemplateSaveError(error.message || sq("pdfTemplateSaveFailed"));
    } finally {
      setPdfTemplateSaving(false);
    }
  }

  function openAddTermForm() {
    setTermDraft({ id: "", title: "New term", text: "", order: terms.length + 1, status: "Active", quotationTypes: [] });
    setAddTermFormOpen(true);
  }

  function closeAddTermForm() {
    setAddTermFormOpen(false);
    setTermDraft({ id: "", title: "New term", text: "", order: terms.length + 1, status: "Active", quotationTypes: [] });
  }

  function patchTermLocal(termId, updates) {
    setTerms((current) =>
      current.map((term) => (term.id === termId ? { ...term, ...updates } : term))
    );
  }

  async function refreshTermsList() {
    const response = await getAllTerms();
    const nextTerms = sortTermsForUi(extractApiTerms(response?.data));
    setTerms(nextTerms);
    return nextTerms;
  }

  async function saveTermInline(term) {
    if (!term || termSaving) return;

    const quotationTypes = normalizeTermQuotationTypes(term);
    const payload = buildTermApiPayload({
      ...term,
      title: String(term.title || "").trim() || "New term",
      text: String(term.text || "").trim(),
      order: Number(term.order) || terms.length + 1,
      status: term.status === "Inactive" ? "Inactive" : "Active",
      quotationTypes,
    });

    if (!payload.title || !payload.description) {
      setTermsError("Title and term text are required.");
      return;
    }
    if (!payload.isSupply && !payload.isSitc && !payload.isService) {
      setTermsError(sq("selectQuotationTypeForTerm"));
      return;
    }

    const existingId = extractNumericId(term.id);
    setTermSaving(true);
    setTermsError("");

    try {
      if (existingId) {
        await updateTerm(existingId, { ...payload, id: existingId });
      } else {
        await createTerm(payload);
      }
      await refreshTermsList();
      if (!existingId) closeAddTermForm();
      showSuccess(existingId ? "Standard term updated." : "Standard term saved.");
    } catch (error) {
      setTermsError(error.message || "Failed to save standard term.");
    } finally {
      setTermSaving(false);
    }
  }

  async function toggleTermStatus(term) {
    if (!term?.id || termSaving) return;
    const numericId = extractNumericId(term.id);
    if (!numericId) return;

    const nextStatus = term.status === "Active" ? "Inactive" : "Active";
    patchTermLocal(term.id, { status: nextStatus });
    setTermSaving(true);
    setTermsError("");

    try {
      await updateTerm(numericId, {
        ...buildTermApiPayload({ ...term, status: nextStatus }),
        isActive: nextStatus === "Active",
      });
      await refreshTermsList();
      showSuccess(nextStatus === "Active" ? "Standard term activated." : "Standard term deactivated.");
    } catch (error) {
      patchTermLocal(term.id, { status: term.status });
      setTermsError(error.message || "Failed to update standard term status.");
    } finally {
      setTermSaving(false);
    }
  }

  async function saveTerm(overrideDraft) {
    await saveTermInline(overrideDraft || termDraft);
  }

  async function removeTerm(id) {
    const numericId = extractNumericId(id);
    if (!numericId) {
      // Unsaved draft / local-only row
      if (String(id || "").startsWith("term-") || !id) {
        setTerms((current) => current.filter((term) => term.id !== id));
      } else {
        setTermsError("Invalid term id.");
      }
      return;
    }

    const term = terms.find((item) => String(item.id) === String(id));
    setTermDeleteTarget({ id, numericId, title: term?.title || "this term" });
  }

  async function confirmRemoveTerm() {
    const target = termDeleteTarget;
    if (!target?.numericId) return;

    setTermDeletingId(target.id);
    setTermsError("");

    try {
      await deleteTerm(target.numericId);
      setTermDeleteTarget(null);
      await refreshTermsList();
      showSuccess("Standard term deleted.");
    } catch (error) {
      setTermsError(error.message || "Failed to delete standard term.");
    } finally {
      setTermDeletingId(null);
    }
  }

  function renderPmcPage() {
    const pmcFieldClass =
      "h-9 w-full rounded-[8px] border border-[#d5deec] bg-white px-3 text-[13px] text-[#33425d] outline-hidden transition focus:border-[#9fb4dd]";
    const pmcActionButtonClass =
      "inline-flex h-9 min-w-[118px] items-center justify-center gap-1.5 rounded-[8px] px-3 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60";
    const createLines = (() => {
      const normalized = normalizePmcLineEntries(pmcCreateForm.defaultRows, pmcCreateForm.costHeadType);
      return normalized.length ? normalized : [createBlankPmcLine({ dirty: true, isNew: true })];
    })();
    const createHeadMetricFields = getPmcHeadTypeMetricFields(pmcCreateForm.costHeadType);
    const createHeadMetrics = pickPmcHeadMetricFields(pmcCreateForm, pmcCreateForm.costHeadType);

    return (
      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[#23324c] sm:text-[22px]">{sq("pmcCostHeadsTitle")}</h2>
            <p className="mt-1 max-w-5xl text-[12px] leading-5 text-[#7184a3] sm:text-[13px]">
              {sq("pmcCostHeadsDescription")}
            </p>
          </div>
          <button
            type="button"
            onClick={openAddPmcForm}
            disabled={headSaving || headsLoading}
            className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[8px] bg-[#243c93] px-3 text-[13px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6] ${
              isMobileLayout
                ? "min-h-[44px] w-full self-stretch"
                : "h-9 self-start sm:self-center"
            }`}
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Cost Head</span>
          </button>
        </div>

        {headsError ? (
          <div className="rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2.5 text-[12px] leading-5 text-[#b06c16]">
            <div className="flex items-start justify-between gap-3">
              <p>{headsError}</p>
              <button
                type="button"
                onClick={() => setHeadsError("")}
                className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#b06c16]"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {headsLoading ? (
          <TableSkeleton />
        ) : (
          <div className={`space-y-2.5 ${isMobileLayout ? "space-y-3" : ""}`}>
            {heads.filter((head) => isPersistedPmcHead(head)).map((head) => {
              const headType = canonicalizePmcHeadType(head.costHeadType) || String(head.costHeadType || "").trim();
              const headMetricFields = getPmcHeadTypeMetricFields(head.costHeadType);
              const headMetrics = pickPmcHeadMetricFields(head, head.costHeadType);
              const rows = (() => {
                const normalized = normalizePmcLineEntries(head.defaultRows, head.costHeadType);
                return normalized.length ? normalized : [createBlankPmcLine({ dirty: false, isNew: true, ...headMetrics })];
              })();
              const isActive = head.status !== "Inactive";
              const headKey = String(head.id);
              const isExpanded = !isMobileLayout || expandedPmcHeadIds.has(headKey);
              function togglePmcHeadExpanded() {
                setExpandedPmcHeadIds((current) => {
                  const next = new Set(current);
                  if (next.has(headKey)) next.delete(headKey);
                  else next.add(headKey);
                  return next;
                });
              }
              return (
                <div
                  key={head.id}
                  className="overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white"
                >
                  {isMobileLayout ? (
                    <div className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <input
                          value={head.name}
                          onChange={(event) => patchHeadLocal(head.id, { name: event.target.value })}
                          placeholder="New cost head"
                          className="min-w-0 flex-1 truncate border-0 bg-transparent px-0 text-[14px] font-semibold leading-5 text-[#23324c] outline-hidden placeholder:text-[#96a5bb] focus:border-b focus:border-[#9fb4dd]"
                          {...localizedFieldProps()}
                        />
                        <button
                          type="button"
                          onClick={togglePmcHeadExpanded}
                          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[#6f82a1] transition active:bg-[#eef2f8]"
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? "Collapse cost head" : "Expand cost head"}
                        >
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </button>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {headType ? (
                          <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#c9d6ef] bg-[#eef3ff] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.04em] text-[#2944a8]">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#2944a8]" />
                            <span className="truncate">{headType.replace(/_/g, " ")}</span>
                          </span>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => toggleHeadStatus(head)}
                          disabled={headSaving || headDeletingId === head.id}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.04em] transition disabled:opacity-60 ${
                            isActive
                              ? "border-[#b7e7c8] bg-[#dff8e8] text-[#219653]"
                              : "border-[#d9e1ee] bg-[#eef2f8] text-[#7b8faa]"
                          }`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-[#219653]" : "bg-[#90a2be]"}`} />
                          {isActive ? "Active" : "Inactive"}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeHead(head.id)}
                          disabled={headDeletingId === head.id || headSaving}
                          className="ml-auto grid h-7 w-7 place-items-center rounded-md text-[#8ea0bd] transition active:bg-[#f1f5fb] disabled:opacity-60"
                          aria-label="Delete cost head"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div className="flex items-center gap-2.5 px-3 py-2.5 sm:px-4">
                    <input
                      value={head.name}
                      onChange={(event) => patchHeadLocal(head.id, { name: event.target.value })}
                      placeholder="New cost head"
                      className="min-w-0 flex-1 border-0 bg-transparent px-0 text-[15px] font-semibold text-[#23324c] outline-hidden placeholder:text-[#96a5bb] focus:border-b focus:border-[#9fb4dd]"
                      {...localizedFieldProps()}
                    />
                    <div className="flex shrink-0 items-center gap-1.5">
                      {headType ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c9d6ef] bg-[#eef3ff] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[#2944a8]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#2944a8]" />
                          {headType}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => toggleHeadStatus(head)}
                        disabled={headSaving || headDeletingId === head.id}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] transition disabled:opacity-60 ${
                          isActive
                            ? "border-[#b7e7c8] bg-[#dff8e8] text-[#219653] hover:bg-[#d1f3de]"
                            : "border-[#d9e1ee] bg-[#eef2f8] text-[#7b8faa] hover:bg-[#e5ebf5]"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-[#219653]" : "bg-[#90a2be]"}`} />
                        {isActive ? "Active" : "Inactive"}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeHead(head.id)}
                        disabled={headDeletingId === head.id || headSaving}
                        className="grid h-7 w-7 place-items-center rounded-md text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:opacity-60"
                        aria-label="Delete cost head"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  )}

                  {isExpanded ? (
                  <div className={`border-t border-[#e7edf7] px-3 py-3 sm:px-4 ${isMobileLayout ? "px-3 py-2.5" : ""}`}>
                    <p className={`mb-2.5 text-[11px] leading-5 text-[#8ea0bd] ${isMobileLayout ? "mb-2" : ""}`}>
                      {head.description || buildPmcHeadPresentation(head.name, head.category).description}
                    </p>

                    {headMetricFields.length ? (
                      <div
                        className="mb-3 grid gap-2"
                        style={{
                          gridTemplateColumns: isMobileLayout
                            ? "repeat(2, minmax(0, 1fr))"
                            : `repeat(${Math.min(headMetricFields.length, 3)}, minmax(0, 1fr))`,
                        }}
                      >
                        {headMetricFields.map((field) => (
                          <label key={`${head.id}-head-${field.key}`} className="space-y-1">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                              {field.label}
                            </span>
                            <input
                              value={headMetrics[field.key] ?? ""}
                              onChange={(event) => updateHeadMetricField(head.id, field.key, event.target.value)}
                              inputMode="decimal"
                              placeholder={field.label}
                              className={`w-full rounded-[8px] border border-[#d5deec] bg-white px-3 text-[13px] text-[#33425d] outline-hidden transition focus:border-[#9fb4dd] ${
                                isMobileLayout ? "min-h-[44px]" : "h-9"
                              }`}
                              {...localizedFieldProps()}
                            />
                          </label>
                        ))}
                      </div>
                    ) : null}

                    <div className="space-y-1.5">
                      {rows.map((row, rowIndex) => (
                        <div key={row.clientId || `${head.id}-row-${rowIndex}`} className="rounded-[10px] border border-[#e7edf7] bg-[#fbfcff] p-2.5">
                          <div className="flex items-center gap-2">
                            <GripVertical className="h-3.5 w-3.5 shrink-0 text-[#b8c4d8]" />
                            <input
                              value={row.label ?? ""}
                              onChange={(event) => updateBreakdownRow(head.id, rowIndex, event.target.value)}
                              placeholder="Item"
                              className={`min-h-0 flex-1 rounded-[8px] border border-[#d5deec] bg-white px-3 text-[13px] text-[#33425d] outline-hidden transition focus:border-[#9fb4dd] ${
                                isMobileLayout ? "min-h-[44px]" : "h-9"
                              }`}
                              {...localizedFieldProps()}
                            />
                            <button
                              type="button"
                              onClick={() => removeBreakdownRow(head.id, rowIndex)}
                              className={`grid shrink-0 place-items-center rounded-[8px] border border-[#d5deec] text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] ${
                                isMobileLayout ? "h-10 w-10" : "h-8 w-8"
                              }`}
                              aria-label="Delete row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {headMetricFields.length ? (
                            <div className="mt-1.5 flex flex-wrap gap-1.5 pl-5">
                              {headMetricFields.map((field) => (
                                <span
                                  key={`${row.clientId}-${field.key}`}
                                  className="inline-flex items-center gap-1 rounded-full border border-[#d9e1ee] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#51657f]"
                                >
                                  <span className="text-[#94a5c0]">{field.label}:</span>
                                  {String(row[field.key] ?? headMetrics[field.key] ?? "").trim() || "—"}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>

                    <div
                      className={`mt-3 flex items-center justify-between gap-3 ${
                        isMobileLayout ? "flex-col items-stretch" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => addBreakdownRow(head.id)}
                        className={`${pmcActionButtonClass} border border-dashed border-[#9eb3dc] bg-white text-[#2944a8] hover:bg-[#f7faff] ${
                          isMobileLayout ? "min-h-[44px] w-full" : ""
                        }`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add row</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => saveHeadInline(head)}
                        disabled={headSaving || !String(head.name || "").trim()}
                        className={`${pmcActionButtonClass} bg-[#243c93] text-white hover:bg-[#1d327f] disabled:bg-[#a6b3d6] ${
                          isMobileLayout ? "min-h-[44px] w-full" : ""
                        }`}
                      >
                        <Save className="h-3.5 w-3.5" />
                        <span>{headSaving ? "Saving..." : "Save"}</span>
                      </button>
                    </div>
                  </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {addPmcFormOpen
          ? createPortal(
              <div className="fixed inset-0 z-130" role="dialog" aria-modal="true" aria-labelledby="create-pmc-cost-head-title">
                <div
                  className="absolute inset-0 bg-[#0b1430]/35"
                  onClick={() => {
                    if (!headSaving) closeAddPmcForm();
                  }}
                />
                <div className="absolute left-1/2 top-1/2 flex max-h-[min(90vh,820px)] w-[min(720px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[24px] border border-[#d8e0ed] bg-white shadow-[0_30px_60px_-30px_rgba(30,51,131,0.35)]">
                  <div className="shrink-0 border-b border-[#d9e1ee] px-5 py-4 sm:px-6">
                    <h3 id="create-pmc-cost-head-title" className="text-[18px] font-semibold text-[#23324c]">
                      Create Cost Head
                    </h3>
                    <p className="mt-1 text-[12px] text-[#7c8faa]">
                      Add a PMC cost head with default template lines.
                    </p>
                  </div>

                  <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4 sm:px-6">
                    {headsError ? (
                      <div className="rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2.5 text-[12px] leading-5 text-[#b06c16]">
                        {headsError}
                      </div>
                    ) : null}

                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">Head Name *</span>
                        <input
                          value={pmcCreateForm.headName}
                          onChange={(event) => patchPmcCreateForm({ headName: event.target.value })}
                          disabled={headSaving}
                          placeholder="Enter head name"
                          className={pmcFieldClass}
                          {...localizedFieldProps()}
                        />
                      </label>
                      <div className="space-y-1">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">Head Type *</span>
                        <select
                          value={pmcCreateForm.costHeadType}
                          onChange={(event) => patchPmcCreateForm({ costHeadType: event.target.value })}
                          disabled={headSaving}
                          className={pmcFieldClass}
                          {...localizedFieldProps()}
                        >
                          <option value="">Select head type</option>
                          {PMC_COST_HEAD_TYPE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[#e7edf7] bg-[#f8fbff] px-3 py-2.5">
                      <span className="text-[12px] font-semibold text-[#51657f]">Status</span>
                      <button
                        type="button"
                        onClick={() => patchPmcCreateForm({ isActive: !pmcCreateForm.isActive })}
                        disabled={headSaving}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] transition disabled:opacity-60 ${
                          pmcCreateForm.isActive
                            ? "border-[#b7e7c8] bg-[#dff8e8] text-[#219653] hover:bg-[#d1f3de]"
                            : "border-[#d9e1ee] bg-[#eef2f8] text-[#7b8faa] hover:bg-[#e5ebf5]"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${pmcCreateForm.isActive ? "bg-[#219653]" : "bg-[#90a2be]"}`} />
                        {pmcCreateForm.isActive ? "Active" : "Inactive"}
                      </button>
                    </div>

                    {createHeadMetricFields.length ? (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                          Head Type Values
                        </p>
                        <div
                          className="grid gap-2"
                          style={{ gridTemplateColumns: `repeat(${Math.min(createHeadMetricFields.length, 3)}, minmax(0, 1fr))` }}
                        >
                          {createHeadMetricFields.map((field) => (
                            <label key={`create-head-${field.key}`} className="space-y-1">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                                {field.label}
                              </span>
                              <input
                                value={createHeadMetrics[field.key] ?? ""}
                                onChange={(event) => patchPmcCreateForm({ [field.key]: event.target.value })}
                                disabled={headSaving}
                                inputMode="decimal"
                                placeholder={field.label}
                                className={pmcFieldClass}
                                {...localizedFieldProps()}
                              />
                            </label>
                          ))}
                        </div>
                        <p className="text-[11px] leading-4 text-[#8ea0bd]">
                          These values are saved on the Cost Head and copied to every line item.
                        </p>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">Default Template Lines</p>
                      <div className="space-y-2">
                        {createLines.map((row, rowIndex) => (
                          <div key={row.clientId || `create-line-${rowIndex}`} className="rounded-[10px] border border-[#e7edf7] bg-[#fbfcff] p-2.5">
                            <div className="flex items-center gap-2">
                              <input
                                value={row.label ?? ""}
                                onChange={(event) => updatePmcCreateLine(rowIndex, event.target.value)}
                                disabled={headSaving}
                                placeholder="Line item"
                                className={pmcFieldClass}
                                {...localizedFieldProps()}
                              />
                              <button
                                type="button"
                                onClick={() => removePmcCreateLine(rowIndex)}
                                disabled={headSaving}
                                className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] border border-[#d5deec] text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:opacity-60"
                                aria-label="Delete line"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {createHeadMetricFields.length ? (
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {createHeadMetricFields.map((field) => (
                                  <span
                                    key={`create-${rowIndex}-${field.key}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-[#d9e1ee] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#51657f]"
                                  >
                                    <span className="text-[#94a5c0]">{field.label}:</span>
                                    {String(row[field.key] ?? createHeadMetrics[field.key] ?? "").trim() || "—"}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addPmcCreateLine}
                        disabled={headSaving}
                        className={`${pmcActionButtonClass} border border-dashed border-[#9eb3dc] bg-white text-[#2944a8] hover:bg-[#f7faff]`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add Line</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-[#d9e1ee] px-5 py-4 sm:px-6">
                    <button
                      type="button"
                      onClick={closeAddPmcForm}
                      disabled={headSaving}
                      className={`${pmcActionButtonClass} border border-[#d5deec] bg-white text-[#51657f] hover:bg-[#f7faff]`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={createPmcCostHead}
                      disabled={headSaving}
                      className={`${pmcActionButtonClass} bg-[#243c93] text-white hover:bg-[#1d327f] disabled:bg-[#a6b3d6]`}
                    >
                      <Save className="h-3.5 w-3.5" />
                      <span>{headSaving ? "Saving..." : "Create"}</span>
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }

  function renderNetPricePage() {
    return (
      <div className="space-y-3">
        <div>
          <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[#23324c] sm:text-[22px]">{sq("netPriceConfiguration")}</h2>
          <p className="mt-1 max-w-5xl text-[12px] leading-5 text-[#7184a3] sm:text-[13px]">
            {sq("netPriceDescription")}
          </p>
        </div>

        {netPriceLoading ? (
          <TableSkeleton />
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
            <label className="w-full max-w-xs space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">{sq("netPricePercentage")}</span>
              <input
                value={netPriceConfig.netPricePercentage}
                onChange={(event) => setNetPriceConfig((current) => ({ ...current, netPricePercentage: event.target.value }))}
                placeholder="Enter percentage"
                inputMode="decimal"
                className="h-10 w-full rounded-[10px] border border-[#cfd9ea] bg-white px-3 text-[13px] text-[#23324c] outline-hidden transition focus:border-[#9fb4dd]"
                {...localizedFieldProps()}
              />
            </label>
            <button
              type="button"
              onClick={saveNetPriceConfig}
              disabled={netPriceSaving || netPriceLoading}
              className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-[10px] bg-[#243c93] px-4 text-[12px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6]"
            >
              <Save className="h-3.5 w-3.5" />
              <span>{netPriceSaving ? sq("saving") : sq("save")}</span>
            </button>
          </div>
        )}
        {netPriceError ? (
          <p className="text-[12px] text-[#b35b62]">{netPriceError}</p>
        ) : null}
      </div>
    );
  }

  function renderPdfDownloadRulesPage() {
    return (
      <div className={`space-y-3 ${isMobileLayout ? "space-y-3.5" : ""}`}>
        <div
          className={
            isMobileLayout
              ? "flex flex-col gap-3"
              : "flex flex-wrap items-start justify-between gap-3"
          }
        >
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[#23324c] sm:text-[22px]">
              {sq("pdfDownloadRules")}
            </h2>
            <p className="mt-1 max-w-5xl text-[12px] leading-5 text-[#7184a3] sm:text-[13px]">
              {sq("pdfDownloadRulesDescription")}
            </p>
          </div>
          <div className={isMobileLayout ? "flex w-full justify-end" : ""}>
            <button
              type="button"
              onClick={savePdfDownloadRules}
              disabled={pdfTemplateSaving || pdfTemplateLoading}
              className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[10px] bg-[#243c93] px-4 text-[12px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6] ${
                isMobileLayout ? "min-h-[44px] px-5 text-[13px]" : "h-9"
              }`}
            >
              <Save className="h-3.5 w-3.5" />
              <span>{pdfTemplateSaving ? sq("saving") : sq("save")}</span>
            </button>
          </div>
        </div>

        {pdfTemplateSaveError ? (
          <p className="text-[12px] text-[#b35b62]">{pdfTemplateSaveError}</p>
        ) : null}

        <PdfTemplateTab
          headerImage={pdfHeaderImage}
          footerImage={pdfFooterImage}
          bodyContent={pdfBodyContent}
          setHeaderImage={setPdfHeaderImage}
          setFooterImage={setPdfFooterImage}
          setBodyContent={setPdfBodyContent}
          errors={pdfTemplateErrors}
          setErrors={setPdfTemplateErrors}
          editable
          saving={pdfTemplateSaving}
          loading={pdfTemplateLoading}
          loadError={pdfTemplateLoadError}
        />
      </div>
    );
  }

  function renderRulesPage() {
    const sortedRules = sortApprovalRules(rules);
    const fieldClass =
      "h-9 w-full rounded-[10px] border border-[#d5deec] bg-white px-2.5 text-[12px] text-[#23324c] outline-hidden transition focus:border-[#9fb4dd] disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]";
    const formFieldClass =
      "h-9 w-full rounded-[10px] border border-[#cfd9ea] bg-white px-3 text-[13px] text-[#23324c] outline-hidden transition focus:border-[#9fb4dd] disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]";
    const showSharedApproval = Boolean(ruleDraft.ruleKind);

    function renderThresholdFields({
      title,
      greaterValue,
      lessValue,
      onGreaterChange,
      onLessChange,
      greaterPrefix = "",
      lessSuffix = "",
    }) {
      return (
        <div className="rounded-[10px] border border-[#e7edf7] bg-[#f8fbff] px-3 py-2.5">
          <p className="mb-2 text-[12px] font-semibold text-[#23324c]">{title}</p>
          <div className="grid grid-cols-2 gap-2.5">
            <label className="min-w-0 space-y-1">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                Greater than{greaterPrefix ? ` (${greaterPrefix})` : ""}
              </span>
              <input
                value={greaterValue}
                onChange={onGreaterChange}
                disabled={ruleSaving}
                placeholder="Enter value"
                inputMode="decimal"
                className={formFieldClass}
                {...localizedFieldProps()}
              />
            </label>
            <label className="min-w-0 space-y-1">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                Less than{lessSuffix ? ` (${lessSuffix})` : ""} 
              </span>
              <input
                value={lessValue}
                onChange={onLessChange}
                disabled={ruleSaving}
                placeholder="Optional"
                inputMode="decimal"
                className={formFieldClass}
                {...localizedFieldProps()}
              />
            </label>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[#23324c] sm:text-[20px]">{sq("approvalRules")}</h2>
          <p className="mt-1 max-w-3xl text-[13px] leading-5 text-[#7184a3]">
            Configure Realised Selling and Realised Gross Margin thresholds, then route quotations to the right Approver.
          </p>
        </div>

        {rulesLoading ? (
          <TableSkeleton />
        ) : sortedRules.length === 0 ? (
          <ConfigEmptyState
            title={sq("noApprovalRulesConfigured")}
            body={sq("approvalRulesEmptyBody")}
            actionLabel={sq("addRule")}
            onAction={openAddRuleForm}
          />
        ) : (
          <div className="space-y-2.5">
            <div className="hidden grid-cols-[48px_minmax(0,1.6fr)_140px_168px_112px_88px] items-center gap-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[#94a5c0] xl:grid">
              <span>#</span>
              <span>Conditions</span>
              <span>Needs Approval</span>
              <span>Approver</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>

            {sortedRules.map((rule, index) => {
              const kind = inferApprovalRuleKind(rule);
              const busy = ruleSavingId === rule.id || ruleDeletingId === rule.id || ruleSaving || rulesLoading;
              const showSelling = kind === "selling" || kind === "both";
              const showMargin = kind === "margin" || kind === "both";

              return (
                <div
                  key={rule.id || `rule-${index}`}
                  className="min-h-[96px] rounded-[12px] border border-[#dde5f1] bg-white xl:h-[96px] xl:overflow-hidden"
                >
                  <div className="grid h-full grid-cols-1 gap-3 p-3.5 xl:grid-cols-[48px_minmax(0,1.6fr)_140px_168px_112px_88px] xl:items-center xl:gap-3 xl:px-4 xl:py-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0] xl:hidden">#</span>
                      <span className="text-[16px] font-semibold text-[#8a9bb8]">{rule.displayOrder}</span>
                    </div>

                    <div className="min-w-0 space-y-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0] xl:hidden">Conditions</span>
                      {showSelling ? (
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className="w-[78px] shrink-0 truncate text-[11px] font-medium text-[#51657f]" title="Realised Selling">
                            Selling
                          </span>
                          <input
                            value={rule.sellingPriceMin}
                            onChange={(event) => patchRuleLocal(rule.id, { sellingPriceMin: event.target.value })}
                            placeholder="≥"
                            inputMode="decimal"
                            title={formatRuleThresholdSummary(rule.sellingPriceMin, rule.sellingPriceMax, { currency: true })}
                            className={`${fieldClass} max-w-[88px] text-right`}
                            {...localizedFieldProps()}
                          />
                          <span className="shrink-0 text-[11px] text-[#94a5c0]">–</span>
                          <input
                            value={rule.sellingPriceMax}
                            onChange={(event) => patchRuleLocal(rule.id, { sellingPriceMax: event.target.value })}
                            placeholder="≤"
                            inputMode="decimal"
                            className={`${fieldClass} max-w-[88px] text-right`}
                            {...localizedFieldProps()}
                          />
                        </div>
                      ) : null}
                      {showMargin ? (
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className="w-[78px] shrink-0 truncate text-[11px] font-medium text-[#51657f]" title="Realised Gross Margin">
                            Margin %
                          </span>
                          <input
                            value={rule.marginMin}
                            onChange={(event) => patchRuleLocal(rule.id, { marginMin: event.target.value })}
                            placeholder="≥"
                            inputMode="decimal"
                            title={formatRuleThresholdSummary(rule.marginMin, rule.marginMax, { percent: true })}
                            className={`${fieldClass} max-w-[88px] text-right`}
                            {...localizedFieldProps()}
                          />
                          <span className="shrink-0 text-[11px] text-[#94a5c0]">–</span>
                          <input
                            value={rule.marginMax}
                            onChange={(event) => patchRuleLocal(rule.id, { marginMax: event.target.value })}
                            placeholder="≤"
                            inputMode="decimal"
                            className={`${fieldClass} max-w-[88px] text-right`}
                            {...localizedFieldProps()}
                          />
                        </div>
                      ) : null}
                    </div>

                    <div className="min-w-0">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0] xl:hidden">Needs Approval</span>
                      <button
                        type="button"
                        onClick={() => patchRuleLocal(rule.id, { autoApprove: !rule.autoApprove })}
                        disabled={busy}
                        className={`mt-1 inline-flex h-9 w-full items-center justify-center truncate rounded-[10px] px-2 text-[11px] font-semibold transition disabled:opacity-60 xl:mt-0 ${
                          rule.autoApprove ? "bg-[#eef2f8] text-[#7b8faa]" : "bg-[#EDF2F8] text-[#2944a8]"
                        }`}
                      >
                        {rule.autoApprove ? "Auto-approve" : "Needs approval"}
                      </button>
                    </div>

                    <div className="min-w-0">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0] xl:hidden">Approver</span>
                      <FormSelect
                        value={rule.approvalLevel}
                        onChange={(event) => patchRuleLocal(rule.id, { approvalLevel: event.target.value })}
                        disabled={rule.autoApprove || approverOptionsLoading || approverOptions.length === 0 || busy}
                        className={`${fieldClass} mt-1 xl:mt-0`}
                        title={rule.approvalLevel || ""}
                        {...localizedFieldProps()}
                      >
                        {!approverOptions.length ? (
                          <option value="">
                            {approverOptionsLoading ? "Loading..." : "No approvers"}
                          </option>
                        ) : (
                          <option value="">Select Approver</option>
                        )}
                        {approverOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </FormSelect>
                    </div>

                    <div className="min-w-0">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0] xl:hidden">Status</span>
                      <FormSelect
                        value={rule.isActive ? "Active" : "Inactive"}
                        onChange={(event) =>
                          patchRuleLocal(rule.id, { isActive: event.target.value === "Active" })
                        }
                        disabled={busy}
                        className={`${fieldClass} mt-1 xl:mt-0`}
                        {...localizedFieldProps()}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </FormSelect>
                    </div>

                    <div className="flex items-center justify-start gap-2 xl:justify-end">
                      <button
                        type="button"
                        onClick={() => saveRuleInline(rule)}
                        disabled={busy}
                        className="grid h-9 w-9 place-items-center rounded-[10px] border border-[#dde5f1] text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label="Save rule"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRule(rule.id)}
                        disabled={busy}
                        className="grid h-9 w-9 place-items-center rounded-[10px] border border-[#dde5f1] text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label="Delete rule"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!rulesLoading && (
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={openAddRuleForm}
              disabled={ruleSaving || rulesLoading || addRuleFormOpen}
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              <span>{sq("addRule")}</span>
            </button>
            {(approverOptionsError || rulesError) && !addRuleFormOpen ? (
              <div className="text-[12px] text-[#b35b62] sm:text-[13px]">
                {approverOptionsError ? <p>{approverOptionsError}</p> : null}
                {rulesError ? <p className={approverOptionsError ? "mt-1" : ""}>{rulesError}</p> : null}
              </div>
            ) : null}
          </div>
        )}

        {addRuleFormOpen
          ? createPortal(
              <div className="fixed inset-0 z-130" role="dialog" aria-modal="true" aria-labelledby="add-approval-rule-title">
                <div
                  className="absolute inset-0 bg-[#0b1430]/35"
                  onClick={() => {
                    if (!ruleSaving) closeAddRuleForm();
                  }}
                />
                <div className="absolute left-1/2 top-1/2 flex w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[20px] border border-[#d8e0ed] bg-white shadow-[0_30px_60px_-30px_rgba(30,51,131,0.35)]">
                  <div className="shrink-0 border-b border-[#d9e1ee] px-5 py-3.5">
                    <h3 id="add-approval-rule-title" className="text-[17px] font-semibold text-[#23324c]">
                      Add Rule
                    </h3>
                    <p className="mt-0.5 text-[12px] text-[#7c8faa]">
                      Select an approval rule, set thresholds, then choose one Approver.
                    </p>
                  </div>

                  <div className="space-y-3 px-5 py-3.5">
                    {(rulesError || approverOptionsError) ? (
                      <div className="rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2 text-[12px] leading-5 text-[#b06c16]">
                        {rulesError || approverOptionsError}
                      </div>
                    ) : null}

                    <div className="grid gap-2.5 sm:grid-cols-2">
                      <label className="block space-y-1">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                          Approval rule *
                        </span>
                        <FormSelect
                          value={ruleDraft.ruleKind}
                          onChange={(event) => patchRuleDraft({ ruleKind: event.target.value })}
                          disabled={ruleSaving}
                          className={formFieldClass}
                          {...localizedFieldProps()}
                        >
                          <option value="">Select approval rule</option>
                          {APPROVAL_RULE_KIND_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </FormSelect>
                      </label>

                      {ruleDraft.ruleKind ? (
                        <label className="block space-y-1">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                            Status
                          </span>
                          <FormSelect
                            value={ruleDraft.isActive ? "Active" : "Inactive"}
                            onChange={(event) =>
                              patchRuleDraft({ isActive: event.target.value === "Active" })
                            }
                            disabled={ruleSaving}
                            className={formFieldClass}
                            {...localizedFieldProps()}
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </FormSelect>
                        </label>
                      ) : (
                        <div />
                      )}
                    </div>

                    {ruleDraft.ruleKind === "both" ? (
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        {renderThresholdFields({
                          title: "Realised Selling",
                          greaterValue: ruleDraft.sellingPriceMin,
                          lessValue: ruleDraft.sellingPriceMax,
                          onGreaterChange: (event) => patchRuleDraft({ sellingPriceMin: event.target.value }),
                          onLessChange: (event) => patchRuleDraft({ sellingPriceMax: event.target.value }),
                          greaterPrefix: "₹",
                          lessSuffix: "₹",
                        })}
                        {renderThresholdFields({
                          title: "Realised Gross Margin",
                          greaterValue: ruleDraft.marginMin,
                          lessValue: ruleDraft.marginMax,
                          onGreaterChange: (event) => patchRuleDraft({ marginMin: event.target.value }),
                          onLessChange: (event) => patchRuleDraft({ marginMax: event.target.value }),
                          greaterPrefix: "%",
                          lessSuffix: "%",
                        })}
                      </div>
                    ) : null}

                    {ruleDraft.ruleKind === "selling"
                      ? renderThresholdFields({
                          title: "Realised Selling",
                          greaterValue: ruleDraft.sellingPriceMin,
                          lessValue: ruleDraft.sellingPriceMax,
                          onGreaterChange: (event) => patchRuleDraft({ sellingPriceMin: event.target.value }),
                          onLessChange: (event) => patchRuleDraft({ sellingPriceMax: event.target.value }),
                          greaterPrefix: "₹",
                          lessSuffix: "₹",
                        })
                      : null}

                    {ruleDraft.ruleKind === "margin"
                      ? renderThresholdFields({
                          title: "Realised Gross Margin",
                          greaterValue: ruleDraft.marginMin,
                          lessValue: ruleDraft.marginMax,
                          onGreaterChange: (event) => patchRuleDraft({ marginMin: event.target.value }),
                          onLessChange: (event) => patchRuleDraft({ marginMax: event.target.value }),
                          greaterPrefix: "%",
                          lessSuffix: "%",
                        })
                      : null}

                    {showSharedApproval ? (
                      <div className="rounded-[10px] border border-[#e7edf7] bg-[#f8fbff] px-3 py-2.5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-[12px] font-semibold text-[#23324c]">Approver</p>
                          <button
                            type="button"
                            onClick={() => patchRuleDraft({ needsApproval: !ruleDraft.needsApproval })}
                            disabled={ruleSaving}
                            className={`inline-flex h-7 min-w-[120px] items-center justify-center rounded-full px-3 text-[10px] font-bold uppercase tracking-[0.04em] transition disabled:opacity-60 ${
                              ruleDraft.needsApproval
                                ? "bg-[#EDF2F8] text-[#2944a8]"
                                : "bg-[#eef2f8] text-[#8a9bb8]"
                            }`}
                          >
                            {ruleDraft.needsApproval ? "Needs approval" : "Auto-approve"}
                          </button>
                        </div>
                        {ruleDraft.needsApproval ? (
                          <label className="mt-2 block space-y-1">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#94a5c0]">
                              Approver *
                            </span>
                            <FormSelect
                              value={ruleDraft.approvalLevel}
                              onChange={(event) => patchRuleDraft({ approvalLevel: event.target.value })}
                              disabled={ruleSaving || approverOptionsLoading || approverOptions.length === 0}
                              className={formFieldClass}
                              {...localizedFieldProps()}
                            >
                              {!approverOptions.length ? (
                                <option value="">
                                  {approverOptionsLoading ? "Loading approval levels..." : "No approval levels available"}
                                </option>
                              ) : (
                                <option value="">Select Approver</option>
                              )}
                              {approverOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </FormSelect>
                          </label>
                        ) : (
                          <p className="mt-2 text-[12px] text-[#7c8faa]">This rule will auto-approve matching quotations.</p>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[#d9e1ee] px-5 py-3.5">
                    <button
                      type="button"
                      onClick={closeAddRuleForm}
                      disabled={ruleSaving}
                      className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#d5deec] bg-white px-4 text-[12px] font-semibold text-[#51657f] transition hover:bg-[#f7faff] disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveAddRuleForm}
                      disabled={ruleSaving || !ruleDraft.ruleKind}
                      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-[10px] bg-[#243c93] px-4 text-[12px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6]"
                    >
                      <Save className="h-3.5 w-3.5" />
                      <span>{ruleSaving ? "Saving..." : "Save rule"}</span>
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }

  function renderTermsPage() {
    const termTitleInputClass =
      "min-w-0 flex-1 border-0 bg-transparent px-0 py-0 text-[15px] font-semibold leading-tight text-[#23324c] outline-hidden placeholder:text-[#96a5bb] focus:border-b focus:border-[#cfd9ea]";
    const termStatusClass = (status) =>
      `shrink-0 rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] transition disabled:opacity-60 ${
        status === "Active"
          ? "bg-[#dff8e8] text-[#219653] hover:bg-[#d1f3de]"
          : "bg-[#eef2f8] text-[#7b8faa] hover:bg-[#e5ebf5]"
      }`;
    const termSaveButtonClass =
      "inline-flex h-9 min-w-[118px] shrink-0 items-center justify-center gap-1.5 rounded-[8px] bg-[#243c93] px-3 text-[13px] font-semibold text-white transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6]";

    function TermReorderArrows({ onUp, onDown, disableUp, disableDown }) {
      return (
        <div className="flex shrink-0 flex-col items-center justify-center gap-0 leading-none text-[#90a2be] select-none">
          <button
            type="button"
            onClick={onUp}
            disabled={disableUp}
            className="flex h-3 w-4 items-center justify-center text-[12px] leading-none disabled:opacity-35"
            aria-label="Move up"
          >
            ⌃
          </button>
          <button
            type="button"
            onClick={onDown}
            disabled={disableDown}
            className="flex h-3 w-4 items-center justify-center text-[12px] leading-none disabled:opacity-35"
            aria-label="Move down"
          >
            ⌄
          </button>
        </div>
      );
    }

    function renderTermRow({
      title,
      text,
      status,
      quotationTypes = [],
      onTitleChange,
      onTextChange,
      onToggleStatus,
      onToggleQuotationType,
      onSave,
      onDelete,
      disableTitle = false,
      disableText = false,
      disableStatus = false,
      disableTypes = false,
      disableSave = false,
      disableDelete = false,
      disableUp = false,
      disableDown = false,
      onUp,
      onDown,
      saveLabel = "Save",
    }) {
      return (
        <div
          className={`overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white ${
            isMobileLayout
              ? "shadow-[0_8px_20px_-18px_rgba(30,51,131,0.22)]"
              : "shadow-[0_10px_24px_-24px_rgba(30,51,131,0.18)]"
          }`}
        >
          <div
            className={`flex items-center gap-2 border-b border-[#eef1f7] px-3 py-2.5 sm:px-4 ${
              isMobileLayout ? "gap-2.5 px-3.5 py-3" : ""
            }`}
          >
            <TermReorderArrows
              onUp={onUp}
              onDown={onDown}
              disableUp={disableUp}
              disableDown={disableDown}
            />
            <input
              value={title}
              onChange={onTitleChange}
              disabled={disableTitle}
              className={`${termTitleInputClass} ${isMobileLayout ? "text-[14px]" : ""}`}
              placeholder="Term title"
              {...localizedFieldProps()}
            />
            <button
              type="button"
              onClick={onToggleStatus}
              disabled={disableStatus}
              className={`${termStatusClass(status)} ${isMobileLayout ? "min-h-[28px] px-3" : ""}`}
            >
              {status}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={disableDelete}
              className={`grid shrink-0 place-items-center rounded-md text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:opacity-60 ${
                isMobileLayout ? "h-9 w-9" : "h-7 w-7"
              }`}
              aria-label="Delete term"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className={`px-3 py-2.5 sm:px-4 ${isMobileLayout ? "space-y-3 px-3.5 py-3.5" : ""}`}>
            <Textarea
              value={text}
              onChange={onTextChange}
              disabled={disableText}
              className={`min-h-[52px] w-full resize-y rounded-[10px] border border-[#d5deec] bg-white px-3 py-2 text-[13px] leading-5 text-[#6b7f9d] outline-hidden transition focus:border-[#9fb4dd] disabled:bg-[#fbfcfe] disabled:text-[#8ea0bd] ${
                isMobileLayout ? "min-h-[88px] leading-6 text-[#51657f]" : ""
              }`}
              {...localizedFieldProps()}
            />
            <div
              className={`mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between ${
                isMobileLayout ? "mt-0 gap-3.5" : ""
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#8ea0bd]">
                  {sq("applicableQuotationTypes")}
                </p>
                <div className={`flex flex-wrap gap-x-4 gap-y-2 ${isMobileLayout ? "gap-2" : ""}`}>
                  {TERM_QUOTATION_TYPE_VALUES.map((typeValue) => {
                    const checked = quotationTypes.includes(typeValue);
                    return (
                      <label
                        key={typeValue}
                        className={`inline-flex items-center gap-2 text-[12px] font-medium ${
                          isMobileLayout ? "min-h-[40px] rounded-[10px] border border-[#e4eaf4] bg-[#fafbfe] px-3" : ""
                        } ${
                          disableTypes ? "cursor-not-allowed text-[#9aacbf]" : "cursor-pointer text-[#51657f]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disableTypes}
                          onChange={() => onToggleQuotationType?.(typeValue)}
                          className={`rounded border-[#c5cfde] text-[#2944a8] accent-[#2944a8] ${
                            isMobileLayout ? "h-4 w-4" : "h-3.5 w-3.5"
                          }`}
                        />
                        <span>{termQuotationTypeLabel(typeValue)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                onClick={onSave}
                disabled={disableSave}
                className={`${termSaveButtonClass} ${
                  isMobileLayout ? "min-h-[44px] w-full sm:w-auto" : ""
                }`}
              >
                <Save className="h-3.5 w-3.5" />
                <span>{saveLabel}</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`space-y-3 ${isMobileLayout ? "space-y-3.5" : ""}`}>
        <div className={isMobileLayout ? "space-y-1" : ""}>
          <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[#23324c] sm:text-[20px]">{sq("termsLibraryTitle")}</h2>
          <p className="mt-1 max-w-5xl text-[12px] leading-5 text-[#7184a3] sm:text-[13px]">
            {sq("termsLibraryDescription")}
          </p>
        </div>

        {termsError ? (
          <div className="rounded-[10px] border border-[#f0d0a7] bg-[#fff8ed] px-3 py-2.5 text-[12px] leading-5 text-[#b06c16]">
            <div className="flex items-start justify-between gap-3">
              <p>{termsError}</p>
              <button
                type="button"
                onClick={() => setTermsError("")}
                className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#b06c16]"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {termsLoading ? (
          <TableSkeleton />
        ) : terms.length === 0 && !addTermFormOpen ? (
          <ConfigEmptyState
            title={sq("noStandardTermsConfigured")}
            body={sq("standardTermsEmptyBody")}
            actionLabel={sq("addTerm")}
            onAction={() => openAddTermForm()}
          />
        ) : (
          <>
            <div className={`space-y-2.5 ${isMobileLayout ? "space-y-3" : ""}`}>
              {terms.map((term, index) => {
                const label = term.title || term.text.split(/[.;]/)[0] || `Term ${index + 1}`;
                const busy = termSaving || termDeletingId === term.id;
                return (
                  <div key={term.id}>
                    {renderTermRow({
                      title: term.title || label,
                      text: term.text,
                      status: term.status,
                      quotationTypes: normalizeTermQuotationTypes(term),
                      onTitleChange: (event) => patchTermLocal(term.id, { title: event.target.value }),
                      onTextChange: (event) => patchTermLocal(term.id, { text: event.target.value }),
                      onToggleStatus: () => toggleTermStatus(term),
                      onToggleQuotationType: (typeValue) => {
                        patchTermLocal(term.id, {
                          quotationTypes: toggleTermQuotationType(term.quotationTypes, typeValue),
                        });
                      },
                      onSave: () => {
                        const latest = terms.find((item) => item.id === term.id) || term;
                        saveTermInline(latest);
                      },
                      onDelete: () => removeTerm(term.id),
                      disableTitle: busy,
                      disableText: busy,
                      disableStatus: busy,
                      disableTypes: busy,
                      disableSave: busy || !String(term.text || "").trim(),
                      disableDelete: busy,
                      disableUp: index === 0 || termSaving,
                      disableDown: index === terms.length - 1 || termSaving,
                      onUp: () => moveItem(terms, setTerms, index, "up"),
                      onDown: () => moveItem(terms, setTerms, index, "down"),
                      saveLabel: termSaving ? "Saving..." : "Save",
                    })}
                  </div>
                );
              })}

              {addTermFormOpen && (
                <div key="new-term">
                  {renderTermRow({
                    title: termDraft.title,
                    text: termDraft.text,
                    status: "Active",
                    quotationTypes: normalizeTermQuotationTypes(termDraft),
                    onTitleChange: (event) => setTermDraft((current) => ({ ...current, title: event.target.value })),
                    onTextChange: (event) => setTermDraft((current) => ({ ...current, text: event.target.value })),
                    onToggleStatus: () => undefined,
                    onToggleQuotationType: (typeValue) => {
                      setTermDraft((current) => ({
                        ...current,
                        quotationTypes: toggleTermQuotationType(current.quotationTypes, typeValue),
                      }));
                    },
                    onSave: () => saveTerm(termDraft),
                    onDelete: closeAddTermForm,
                    disableTitle: termSaving,
                    disableText: termSaving,
                    disableStatus: true,
                    disableTypes: termSaving,
                    disableSave: termSaving || !String(termDraft.text || "").trim(),
                    disableDelete: termSaving,
                    disableUp: true,
                    disableDown: true,
                    onUp: () => undefined,
                    onDown: () => undefined,
                    saveLabel: termSaving ? "Saving..." : "Save",
                  })}
                </div>
              )}
            </div>

            {!addTermFormOpen && (
              <div className={isMobileLayout ? "pt-0.5" : ""}>
                <button
                  type="button"
                  onClick={() => openAddTermForm()}
                  disabled={termSaving || termsLoading}
                  className={`inline-flex items-center gap-1.5 font-semibold text-[#2944a8] disabled:opacity-60 ${
                    isMobileLayout
                      ? "min-h-[44px] w-full justify-center rounded-[12px] border border-dashed border-[#9eb3dc] bg-white text-[14px]"
                      : "text-[14px]"
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  <span>Add term</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {banner && (
        <div className="rounded-2xl border border-[#d5e8dc] bg-[#f4fff8] px-5 py-4 text-[#2b7a4b]">
          <p className="text-[13px] font-semibold">{banner}</p>
        </div>
      )}

      {activeSection === "pmc" && renderPmcPage()}
      {activeSection === "rules" && renderRulesPage()}
      {activeSection === "terms" && renderTermsPage()}
      {activeSection === "netPrice" && renderNetPricePage()}
      {activeSection === "pdf" && renderPdfDownloadRulesPage()}

      <QuotationConfirmModal
        open={Boolean(termDeleteTarget)}
        title="Delete standard term?"
        description={
          termDeleteTarget
            ? `Delete "${termDeleteTarget.title}"? This cannot be undone.`
            : "Delete this standard term? This cannot be undone."
        }
        confirmLabel={termDeletingId ? "Deleting..." : "Delete"}
        confirming={Boolean(termDeletingId)}
        onConfirm={confirmRemoveTerm}
        onCancel={() => {
          if (termDeletingId) return;
          setTermDeleteTarget(null);
        }}
      />
    </div>
  );
}

function PmcCostHeadsPage() {
  useTranslation();
  const { pathname } = useLocation();
  const activeSection = pathname.includes("/approval-rules")
    ? "rules"
    : pathname.includes("/net-price-configuration")
      ? "netPrice"
    : pathname.includes("/terms-library")
      ? "terms"
    : pathname.includes("/pdf-download-rules")
      ? "pdf"
      : "pmc";
  const pageTitle = activeSection === "rules"
    ? sq("approvalRules")
    : activeSection === "netPrice"
      ? sq("netPriceConfig")
      : activeSection === "terms"
        ? sq("termsLibrary")
        : activeSection === "pdf"
          ? sq("pdfDownloadRules")
        : sq("pmcConfig");

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-6xl">
        {activeSection !== "pmc" && activeSection !== "netPrice" && activeSection !== "terms" && activeSection !== "rules" && activeSection !== "pdf" && (
          <div className="mb-5">
            <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-foreground sm:text-[22px]">{pageTitle}</h2>
            <p className="mt-1 max-w-4xl text-[12px] leading-6 text-[#6d7f9b] sm:text-[13px]">
              {sq("adminSubtitle")}
            </p>
          </div>
        )}
        <ContentCard className="border-transparent bg-transparent shadow-none" bodyClassName="p-0">
          <QuotationConfigurationWorkspace activeSection={activeSection} />
        </ContentCard>
      </div>
    </AppLayout>
  );
}

export {
  PmcCostHeadsPage
};
